import { useMemo } from 'react';
import type { CaseDocumentMeta } from '@voicer/shared';
import { useStore } from '../state/store';

const CATEGORY_ORDER = ['Pleadings', 'Discovery', 'Medical', 'Correspondence', 'Orders', 'Other'];

function categoryRank(c: string): number {
  const i = CATEGORY_ORDER.indexOf(c);
  return i >= 0 ? i : CATEGORY_ORDER.length;
}

export function DocumentBrowser(): React.ReactNode {
  const documents = useStore((s) => s.caseDocuments);
  const selectedId = useStore((s) => s.selectedDocumentId);
  const setSelected = useStore((s) => s.setSelectedDocumentId);

  const groups = useMemo(() => {
    const map = new Map<string, CaseDocumentMeta[]>();
    for (const d of documents) {
      const key = d.category || 'Other';
      const list = map.get(key) ?? [];
      list.push(d);
      map.set(key, list);
    }
    return [...map.entries()]
      .sort((a, b) => categoryRank(a[0]) - categoryRank(b[0]) || a[0].localeCompare(b[0]))
      .map(([category, docs]) => ({
        category,
        docs: [...docs].sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title)),
      }));
  }, [documents]);

  if (!documents.length) {
    return (
      <div className="doc-browser">
        <div className="doc-browser-head">Documents</div>
        <p className="dim doc-empty">No documents for this case.</p>
      </div>
    );
  }

  return (
    <div className="doc-browser">
      <div className="doc-browser-head">Documents</div>
      <div className="doc-tree">
        {groups.map((g) => (
          <section key={g.category} className="doc-group">
            <h4 className="doc-group-title">{g.category}</h4>
            <ul className="doc-list">
              {g.docs.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    className={selectedId === d.id ? 'doc-item active' : 'doc-item'}
                    onClick={() => setSelected(selectedId === d.id ? null : d.id)}
                    title={d.summary}
                  >
                    <span className="doc-item-title">{d.title}</span>
                    {d.stageLabel ? <span className="dim doc-item-meta">{d.stageLabel}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
